"use client";

import { DexProtocol } from "@cede/types";
import { EXCHANGES } from "@cede/utils";
import { UnifiedMarketType } from "@/types/trading";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface SelectedCexSpotMarket {
  type: UnifiedMarketType.SPOT;
  exchangeId: string;
  baseAsset: string;
  quoteAsset: string;
  pairSymbol: string;
}

interface SelectedPerpsMarket {
  type: UnifiedMarketType.PERPS;
  symbol: string;
  exchangeId: string;
  baseAsset: string;
  quoteAsset: string;
  externalMarketId: string;
}

interface SelectedDexMarket {
  type: UnifiedMarketType.DEX;
  dexProtocol: DexProtocol;
  chainId: string;
  pairSymbol: string;
  baseAsset: string; // Symbol for display (e.g., "USDC")
  quoteAsset: string; // Symbol for display (e.g., "AERO")
  baseAssetAddress: string; // Token address for baseAsset (tokenOut - what you're buying)
  quoteAssetAddress: string; // Token address for quoteAsset (tokenIn - what you're selling)
}

export type SelectedMarket = SelectedCexSpotMarket | SelectedPerpsMarket | SelectedDexMarket;

interface UnifiedTradeState {
  selectedMarket: SelectedMarket | null;
  pairSymbol: string | null;
  setSelectedCexSpotMarket: (exchangeId: string, baseAsset: string, quoteAsset: string, pairSymbol: string) => void;
  setSelectedPerpsMarket: (
    symbol: string,
    exchangeId: string,
    baseAsset: string,
    quoteAsset: string,
    externalMarketId: string,
  ) => void;
  setSelectedDexMarket: (
    dexProtocol: DexProtocol,
    chainId: string,
    pairSymbol: string,
    baseAsset: string,
    quoteAsset: string,
    baseAssetAddress: string,
    quoteAssetAddress: string,
  ) => void;
  clearSelectedMarket: () => void;
}

// Default market - ALWAYS BTC-PERP on Hyperliquid
const DEFAULT_MARKET: SelectedPerpsMarket = {
  type: UnifiedMarketType.PERPS,
  symbol: "BTC-PERP",
  exchangeId: EXCHANGES.HYPERLIQUID,
  baseAsset: "BTC",
  quoteAsset: "USD",
  externalMarketId: "PF_XBTUSD",
};

/**
 * Unified Trade Store - Single Source of Truth
 *
 * This is the ONLY place that stores the currently selected market.
 * All other components and stores should read from here.
 *
 * Default: PERPS + BTC-PERP (never persisted, always resets to default)
 */
export const useUnifiedTradeStore = create<UnifiedTradeState>()(
  persist(
    (set) => ({
      selectedMarket: null,
      pairSymbol: null,

      setSelectedCexSpotMarket: (exchangeId, baseAsset, quoteAsset, pairSymbol) => {
        set({
          selectedMarket: {
            type: UnifiedMarketType.SPOT,
            exchangeId,
            baseAsset,
            quoteAsset,
            pairSymbol,
          },
          pairSymbol,
        });
      },

      setSelectedPerpsMarket: (symbol, exchangeId, baseAsset, quoteAsset, externalMarketId) => {
        set({
          selectedMarket: {
            type: UnifiedMarketType.PERPS,
            symbol,
            exchangeId,
            baseAsset,
            quoteAsset,
            externalMarketId,
          },
          pairSymbol: symbol,
        });
      },

      setSelectedDexMarket: (
        dexProtocol,
        chainId,
        pairSymbol,
        baseAsset,
        quoteAsset,
        baseAssetAddress,
        quoteAssetAddress,
      ) => {
        set({
          selectedMarket: {
            type: UnifiedMarketType.DEX,
            dexProtocol,
            chainId,
            pairSymbol,
            baseAsset,
            quoteAsset,
            baseAssetAddress,
            quoteAssetAddress,
          },
          pairSymbol,
        });
      },

      clearSelectedMarket: () => {
        set({ selectedMarket: DEFAULT_MARKET });
      },
    }),
    {
      name: "unified-trade-market",
      // Don't persist - always default to BTC-PERP
      partialize: () => ({}),
    },
  ),
);

/**
 * Hook for accessing unified trade store
 * Provides backward compatibility with previous context-based implementation
 */
export const useUnifiedTrade = useUnifiedTradeStore;
